import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, ApiClientError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useToast } from '../../components/Toast';
import Icon from '../../components/Icon';
import { Badge, EmptyState, PageLoading, Banner } from '../../components/ui';
import { PageHeader } from '../../pages/AppShell';

import MapCanvas from './MapCanvas';
import VisitDetail from './VisitDetail';
import {
  OUTCOME_LABEL, clockTime, daysAgo, duration, flagInfo, today, useCompanyTimeZone,
} from './visitMeta';

import type {
  CompanySettings, CoverageReport, DayMap, TeamMap, VisitList,
} from '../../types';

type Tab = 'coverage' | 'map' | 'list';

/**
 * The console's visits screen.
 *
 * This is the one place where the two kinds of business genuinely
 * diverge, and the screen leads with whichever question that business
 * actually asks:
 *
 *   goods    "Was the beat covered?" — a table, reps down, days across.
 *   service  "Where was everyone?"   — the map, the day in order beside it.
 *
 * Same records underneath. Both tabs stay reachable either way, because a
 * distributor sometimes wants the map and a service manager sometimes
 * wants the table; only which one opens first changes.
 */
export default function VisitsPage() {
  const { can } = useAuth();
  const tz = useCompanyTimeZone();
  const toast = useToast();
  const canReview = can('manager', 'company_admin', 'platform_admin');

  const [settings, setSettings] = useState<CompanySettings | null>(null);
  const [tab, setTab] = useState<Tab | null>(null);
  const [openVisit, setOpenVisit] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const [from, setFrom] = useState(() => daysAgo(6, tz));
  const [to, setTo] = useState(() => today(tz));
  const [day, setDay] = useState(() => today(tz));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await api.get<CompanySettings>('/settings');
        if (cancelled) return;
        setSettings(data);
        // Lead with the question this business asks.
        setTab(data.businessModel === 'goods' ? 'coverage' : 'map');
      } catch (err) {
        if (!cancelled) {
          toast.error(err instanceof ApiClientError ? err.message : 'Could not load settings.');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [toast]);

  const refresh = useCallback(() => setReloadKey((n) => n + 1), []);

  if (!settings || !tab) {
    return (
      <>
        <PageHeader title="Visits" />
        <div className="page"><PageLoading /></div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Visits"
        subtitle={settings.businessModel === 'goods'
          ? 'Every call your team made, and how much of the beat it covered.'
          : 'Where your team was today, in the order they were there.'}
      />

      <div className="page col gap-16">
        <div className="tabs">
          <button
            type="button"
            className={`tab${tab === 'coverage' ? ' active' : ''}`}
            onClick={() => setTab('coverage')}
          >
            Coverage
          </button>
          <button
            type="button"
            className={`tab${tab === 'map' ? ' active' : ''}`}
            onClick={() => setTab('map')}
          >
            Day map
          </button>
          <button
            type="button"
            className={`tab${tab === 'list' ? ' active' : ''}`}
            onClick={() => setTab('list')}
          >
            All visits
          </button>
        </div>

        {tab === 'coverage' ? (
          <CoverageTab from={from} to={to} setFrom={setFrom} setTo={setTo} key={`c${reloadKey}`} />
        ) : null}

        {tab === 'map' ? (
          <MapTab
            day={day}
            setDay={setDay}
            onOpen={setOpenVisit}
            key={`m${reloadKey}`}
          />
        ) : null}

        {tab === 'list' ? (
          <ListTab from={from} to={to} setFrom={setFrom} setTo={setTo}
            onOpen={setOpenVisit} key={`l${reloadKey}`} />
        ) : null}
      </div>

      {openVisit ? (
        <VisitDetail
          visitId={openVisit}
          canReview={canReview}
          onClose={() => setOpenVisit(null)}
          onReviewed={refresh}
        />
      ) : null}
    </>
  );
}

// =====================================================================

function DateRange({
  from, to, setFrom, setTo,
}: { from: string; to: string; setFrom: (v: string) => void; setTo: (v: string) => void }) {
  const tz = useCompanyTimeZone();
  return (
    <div className="row gap-8">
      <input type="date" className="input" value={from} max={to}
        onChange={(e) => setFrom(e.target.value)} aria-label="From" />
      <span className="faint small">to</span>
      <input type="date" className="input" value={to} min={from} max={today(tz)}
        onChange={(e) => setTo(e.target.value)} aria-label="To" />
      <button type="button" className="btn btn-ghost btn-sm"
        onClick={() => { setFrom(daysAgo(6, tz)); setTo(today(tz)); }}>
        Last 7 days
      </button>
      <button type="button" className="btn btn-ghost btn-sm"
        onClick={() => { setFrom(daysAgo(29, tz)); setTo(today(tz)); }}>
        Last 30 days
      </button>
    </div>
  );
}

function CoverageTab({
  from, to, setFrom, setTo,
}: { from: string; to: string; setFrom: (v: string) => void; setTo: (v: string) => void }) {
  const toast = useToast();
  const [report, setReport] = useState<CoverageReport | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const data = await api.get<CoverageReport>('/visits/reports/coverage', { from, to });
        if (!cancelled) setReport(data);
      } catch (err) {
        if (!cancelled) {
          toast.error(err instanceof ApiClientError ? err.message : 'Could not load coverage.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [from, to, toast]);

  const days = useMemo(() => {
    const out: string[] = [];
    for (let d = new Date(`${from}T12:00:00Z`); d <= new Date(`${to}T12:00:00Z`);
      d = new Date(d.getTime() + 86400000)) {
      out.push(d.toISOString().slice(0, 10));
    }
    return out;
  }, [from, to]);

  const byCell = useMemo(() => {
    const map = new Map<string, { visits: number; productive: number }>();
    for (const cell of report?.grid ?? []) {
      map.set(`${cell.userId}|${cell.date.slice(0, 10)}`, cell);
    }
    return map;
  }, [report]);

  return (
    <div className="col gap-16">
      <div className="toolbar">
        <DateRange from={from} to={to} setFrom={setFrom} setTo={setTo} />
      </div>

      {loading ? <PageLoading /> : !report ? null : (
        <>
          {report.note ? <Banner kind="info">{report.note}</Banner> : null}

          {report.reps.length === 0 ? (
            <div className="card">
              <EmptyState
                icon="map"
                title="No visits in this range"
                body="Once your team starts checking in at shops, their calls appear here."
              />
            </div>
          ) : (
            <>
              <div className="stat-row">
                <Stat label="Visits" value={report.totals.visits.toLocaleString()} icon="map" />
                <Stat
                  label="Productive"
                  value={report.totals.visits
                    ? `${Math.round((report.totals.productive / report.totals.visits) * 100)}%`
                    : '—'}
                  icon="check"
                />
                <Stat
                  label="Far from the shop"
                  value={report.totals.farVisits.toLocaleString()}
                  icon="pin"
                  tone={report.totals.farVisits > 0 ? 'warning' : undefined}
                />
              </div>

              <div className="card">
                <div className="card-head">
                  <h3>By rep</h3>
                  <span className="small faint">{report.from} to {report.to}</span>
                </div>
                <div className="table-wrap">
                  <table className="data">
                    <thead>
                      <tr>
                        <th>Rep</th>
                        <th className="num">Visits</th>
                        <th className="num">Shops</th>
                        <th className="num">Days</th>
                        <th className="num">Per day</th>
                        <th className="num">Avg on site</th>
                        <th className="num">Productive</th>
                        {report.hasPlan ? <th className="num">On plan</th> : null}
                        {report.hasPlan ? <th className="num">Coverage</th> : null}
                        <th className="num">Far away</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.reps.map((rep) => (
                        <tr key={rep.userId}>
                          <td>{rep.name}</td>
                          <td className="num">{rep.visits}</td>
                          <td className="num">{rep.uniqueCustomers}</td>
                          <td className="num">{rep.daysWorked}</td>
                          <td className="num">{rep.avgPerDay}</td>
                          <td className="num">
                            {rep.avgMinutesOnSite != null ? `${rep.avgMinutesOnSite} min` : '—'}
                          </td>
                          <td className="num">
                            {rep.productivePct != null ? `${rep.productivePct}%` : '—'}
                          </td>
                          {report.hasPlan ? (
                            <td className="num">
                              {rep.onPlan} / {rep.planned}
                            </td>
                          ) : null}
                          {report.hasPlan ? (
                            <td className="num">
                              {rep.coveragePct != null ? (
                                <Badge tone={rep.coveragePct >= 80 ? 'success'
                                  : rep.coveragePct >= 50 ? 'warning' : 'danger'}>
                                  {rep.coveragePct}%
                                </Badge>
                              ) : '—'}
                            </td>
                          ) : null}
                          <td className="num">
                            {rep.farVisits > 0
                              ? <Badge tone="warning">{rep.farVisits}</Badge>
                              : <span className="faint">0</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {report.hasPlan ? (
                  <div className="card-pad small faint" style={{ paddingTop: 0 }}>
                    On-plan is recorded when a rep checks in, not worked out
                    afterwards — so visits made before you set a plan count as
                    off-plan, and coverage for those days reads low until the
                    plan has been in place for a full week.
                  </div>
                ) : null}
              </div>

              {days.length <= 40 ? (
                <div className="card">
                  <div className="card-head">
                    <h3>Day by day</h3>
                    <span className="small faint">Calls made, productive ones in bold</span>
                  </div>
                  <div className="table-wrap">
                    <table className="data grid-table">
                      <thead>
                        <tr>
                          <th>Rep</th>
                          {days.map((d) => (
                            <th key={d} className="num" title={d}>
                              {new Date(`${d}T12:00:00Z`).toLocaleDateString([], {
                                day: 'numeric', month: 'short',
                              })}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {report.reps.map((rep) => (
                          <tr key={rep.userId}>
                            <td className="nowrap">{rep.name}</td>
                            {days.map((d) => {
                              const cell = byCell.get(`${rep.userId}|${d}`);
                              return (
                                <td key={d} className="num">
                                  {cell ? (
                                    <span title={`${cell.productive} productive`}>
                                      <strong>{cell.productive}</strong>
                                      <span className="faint">/{cell.visits}</span>
                                    </span>
                                  ) : <span className="faint">·</span>}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : null}
            </>
          )}
        </>
      )}
    </div>
  );
}

// =====================================================================

function MapTab({
  day, setDay, onOpen,
}: { day: string; setDay: (v: string) => void; onOpen: (id: string) => void }) {
  const tz = useCompanyTimeZone();
  const toast = useToast();
  const [team, setTeam] = useState<TeamMap | null>(null);
  const [repId, setRepId] = useState<string | null>(null);
  const [detail, setDetail] = useState<DayMap | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const data = await api.get<TeamMap>('/visits/reports/team-map', { date: day });
        if (cancelled) return;
        setTeam(data);
        setRepId((current) => (
          current && data.reps.some((r) => r.userId === current)
            ? current
            : data.reps[0]?.userId ?? null
        ));
      } catch (err) {
        if (!cancelled) {
          toast.error(err instanceof ApiClientError ? err.message : 'Could not load the map.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [day, toast]);

  useEffect(() => {
    if (!repId) { setDetail(null); return undefined; }
    let cancelled = false;
    (async () => {
      try {
        const data = await api.get<DayMap>('/visits/reports/day-map', { date: day, userId: repId });
        if (!cancelled) setDetail(data);
      } catch {
        if (!cancelled) setDetail(null);
      }
    })();
    return () => { cancelled = true; };
  }, [repId, day]);

  const selected = team?.reps.find((r) => r.userId === repId) ?? null;

  return (
    <div className="col gap-16">
      <div className="toolbar">
        <div className="row gap-8">
          <input type="date" className="input" value={day} max={today(tz)}
            onChange={(e) => setDay(e.target.value)} aria-label="Day" />
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setDay(today(tz))}>
            Today
          </button>
        </div>
      </div>

      {loading ? <PageLoading /> : !team || team.reps.length === 0 ? (
        <div className="card">
          <EmptyState
            icon="map"
            title="Nobody recorded a visit on this day"
            body="Visits with a location appear here, in the order they happened."
          />
        </div>
      ) : (
        <div className="day-map">
          <div className="card">
            <div className="card-head">
              <h3>Who was out</h3>
              <span className="small faint">{team.reps.length} reps</span>
            </div>
            <div className="rep-list">
              {team.reps.map((rep) => (
                <button
                  key={rep.userId}
                  type="button"
                  className={`rep-row${rep.userId === repId ? ' active' : ''}`}
                  onClick={() => setRepId(rep.userId)}
                >
                  <span className="grow nowrap">{rep.name}</span>
                  <Badge tone="neutral">{rep.points.length}</Badge>
                </button>
              ))}
            </div>
          </div>

          <div className="col gap-16">
            <div className="card">
              <div className="card-head">
                <h3>{selected?.name ?? 'Day'}</h3>
                {detail ? (
                  <span className="small faint">
                    {detail.summary.visits} stops · {detail.summary.minutesOnSite} min on site ·
                    {' '}{detail.summary.straightLineKm} km apart
                  </span>
                ) : null}
              </div>
              <div className="card-pad">
                <MapCanvas
                  points={selected?.points ?? []}
                  onSelect={onOpen}
                  height={380}
                />
                <p className="small faint" style={{ marginTop: 10 }}>
                  Plotted from the coordinates each phone recorded. Distances are
                  straight lines, not road distance.
                </p>
              </div>
            </div>

            {detail?.attendance ? (
              <Banner kind="info">
                Checked in at {clockTime(detail.attendance.firstInAt, tz)} and out at{' '}
                {clockTime(detail.attendance.lastOutAt, tz)} — {detail.attendance.workedMinutes} minutes
                on the clock against {detail.summary.minutesOnSite} minutes on site.
              </Banner>
            ) : null}

            <div className="card">
              <div className="card-head"><h3>The day in order</h3></div>
              <div className="table-wrap">
                <table className="data">
                  <thead>
                    <tr>
                      <th className="num">#</th>
                      <th>Where</th>
                      <th>In</th>
                      <th className="num">On site</th>
                      <th>Outcome</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(selected?.points ?? []).map((p) => (
                      <tr key={p.visitId} onClick={() => onOpen(p.visitId)} className="clickable">
                        <td className="num">{p.seq}</td>
                        <td>{p.label}</td>
                        <td>{clockTime(p.startedAt, tz)}</td>
                        <td className="num">{duration(p.durationSeconds)}</td>
                        <td>
                          {p.outcome ? (
                            <Badge tone={p.isProductive ? 'success' : 'neutral'}>
                              {OUTCOME_LABEL[p.outcome]}
                            </Badge>
                          ) : <span className="faint">In progress</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// =====================================================================

function ListTab({
  from, to, setFrom, setTo, onOpen,
}: {
  from: string; to: string;
  setFrom: (v: string) => void; setTo: (v: string) => void;
  onOpen: (id: string) => void;
}) {
  const tz = useCompanyTimeZone();
  const toast = useToast();
  const [list, setList] = useState<VisitList | null>(null);
  const [loading, setLoading] = useState(true);
  const [flaggedOnly, setFlaggedOnly] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const data = await api.get<VisitList>('/visits', {
          from, to, flaggedOnly: flaggedOnly ? 'true' : undefined, limit: 100,
        });
        if (!cancelled) setList(data);
      } catch (err) {
        if (!cancelled) {
          toast.error(err instanceof ApiClientError ? err.message : 'Could not load visits.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [from, to, flaggedOnly, toast]);

  return (
    <div className="col gap-16">
      <div className="toolbar between">
        <DateRange from={from} to={to} setFrom={setFrom} setTo={setTo} />
        <button
          type="button"
          className={`btn btn-sm ${flaggedOnly ? 'btn-primary' : 'btn-ghost'}`}
          onClick={() => setFlaggedOnly((v) => !v)}
        >
          <Icon name="alert" size={15} /> Needs a look
        </button>
      </div>

      {loading ? <PageLoading /> : !list || list.visits.length === 0 ? (
        <div className="card">
          <EmptyState
            icon="list"
            title={flaggedOnly ? 'Nothing needs a look' : 'No visits in this range'}
            body={flaggedOnly
              ? 'Visits are flagged automatically — a wrong pin, a very short call, an unsure face match.'
              : undefined}
          />
        </div>
      ) : (
        <div className="card">
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Rep</th>
                  <th>Where</th>
                  <th>In</th>
                  <th className="num">On site</th>
                  <th>Outcome</th>
                  <th>Flags</th>
                </tr>
              </thead>
              <tbody>
                {list.visits.map((v) => (
                  <tr key={v.id} className="clickable" onClick={() => onOpen(v.id)}>
                    <td className="nowrap">{v.visitDate}</td>
                    <td className="nowrap">{v.user?.name ?? '—'}</td>
                    <td>
                      {v.customer?.name ?? v.lead?.name ?? v.siteLabel ?? <span className="faint">Unnamed site</span>}
                      {v.lead ? <span className="small faint"> · lead</span> : null}
                      {v.customer?.route ? (
                        <div className="small faint">{v.customer.route}</div>
                      ) : null}
                    </td>
                    <td className="nowrap">{clockTime(v.startedAt, tz)}</td>
                    <td className="num">{duration(v.durationSeconds)}</td>
                    <td>
                      {v.outcome ? (
                        <Badge tone={v.isProductive ? 'success' : 'neutral'}>
                          {OUTCOME_LABEL[v.outcome]}
                        </Badge>
                      ) : <span className="faint">In progress</span>}
                    </td>
                    <td>
                      <div className="row gap-4 wrap">
                        {v.flags.slice(0, 2).map((flag) => {
                          const info = flagInfo(flag);
                          return (
                            <Badge key={flag} tone={info.tone === 'danger' ? 'danger'
                              : info.tone === 'warning' ? 'warning' : 'neutral'}>
                              {info.label}
                            </Badge>
                          );
                        })}
                        {v.flags.length > 2 ? (
                          <span className="small faint">+{v.flags.length - 2}</span>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({
  label, value, icon, tone,
}: { label: string; value: string; icon: 'map' | 'check' | 'pin'; tone?: 'warning' }) {
  return (
    <div className="stat">
      <div className="stat-icon"><Icon name={icon} size={18} /></div>
      <div>
        <div className="stat-label">{label}</div>
        <div className={`stat-value${tone ? ` ${tone}` : ''}`}>{value}</div>
      </div>
    </div>
  );
}
